import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { BsClock } from "react-icons/bs";
import { GiStarShuriken } from "react-icons/gi";
import TestMultiSelect from "./TestMultiSelect";
import type { SelectedSubject } from "./testTypes";
import {
  fetchQuestionCountsBySubtopics,
  fetchSubtopicsForTopic,
  fetchTopicsForSubject,
  type ContentSubtopic,
  type ContentTopic,
  type LevelCountMap,
} from "../../../utils/contentUtils";
import authService from "../../../utils/authService";
import { TEST_FLOW_API } from "./testApiPaths";

const LEVELS = ["level1", "level2", "level3"] as const;
const LEVEL_LABELS: Record<(typeof LEVELS)[number], string> = {
  level1: "Easy",
  level2: "Medium",
  level3: "Hard",
};

export type SubtopicLevelCounts = {
  level: string;
  mcq_count: number;
  coding_count: number;
};

export type SubtopicQuestionRule = {
  subject_id: string;
  topic_id: string;
  sub_topic_id: string;
  subject_name?: string;
  topic_name?: string;
  sub_topic_name?: string;
  level_counts: SubtopicLevelCounts[];
};

export type QuestionSelectionLogic = {
  subject_ids: string[];
  topic_ids: string[];
  sub_topic_ids: string[];
  subtopic_rules: SubtopicQuestionRule[];
};

type Availability = { mcq: LevelCountMap; coding: LevelCountMap };

type LmsRuleRow = {
  level: string;
  score: string | number;
  time: string | number;
};

type LmsRulesByType = {
  mcq?: LmsRuleRow[];
  coding?: LmsRuleRow[];
};

const normalizeRuleLevel = (level: string) =>
  String(level || "").replace(/\s+/g, "").toLowerCase();

const ruleNumeric = (row: LmsRuleRow | undefined, field: "score" | "time") => {
  if (!row) return 0;
  const n = Number(row[field]);
  return Number.isFinite(n) ? n : 0;
};

const lookupLmsRule = (rows: LmsRuleRow[] | undefined, level: string) =>
  rows?.find((r) => normalizeRuleLevel(r.level) === normalizeRuleLevel(level));

export const computePaperTotalsFromLogic = (
  logic: Pick<QuestionSelectionLogic, "subtopic_rules"> | null | undefined,
  lmsRules: LmsRulesByType | null | undefined
): { minutes: number; marks: number } => {
  let minutes = 0;
  let marks = 0;
  (logic?.subtopic_rules || []).forEach((rule) => {
    (rule.level_counts || []).forEach((lc) => {
      const mcqRule = lookupLmsRule(lmsRules?.mcq, lc.level);
      const codingRule = lookupLmsRule(lmsRules?.coding, lc.level);
      minutes +=
        Number(lc.mcq_count || 0) * ruleNumeric(mcqRule, "time") +
        Number(lc.coding_count || 0) * ruleNumeric(codingRule, "time");
      marks +=
        Number(lc.mcq_count || 0) * ruleNumeric(mcqRule, "score") +
        Number(lc.coding_count || 0) * ruleNumeric(codingRule, "score");
    });
  });
  return { minutes, marks };
};

type QuestionSelectionLogicModalProps = {
  show: boolean;
  testId: string;
  testName?: string;
  testType?: string | null;
  subjects: SelectedSubject[];
  batchNames?: string[];
  /** Final Test: course-defined logic is view-only */
  readOnly?: boolean;
  onHide: () => void;
  onSaved: (
    logic: QuestionSelectionLogic,
    description?: string,
    totals?: { duration: number; marks: number }
  ) => void;
};

export const emptyLogic = (): QuestionSelectionLogic => ({
  subject_ids: [],
  topic_ids: [],
  sub_topic_ids: [],
  subtopic_rules: [],
});

export const defaultLevelCounts = (): SubtopicLevelCounts[] =>
  LEVELS.map((level) => ({ level, mcq_count: 0, coding_count: 0 }));

const logicEquals = (a: QuestionSelectionLogic, b: QuestionSelectionLogic) =>
  JSON.stringify(a) === JSON.stringify(b);

const allowsMultiSubject = (testType?: string | null) => {
  const t = (testType || "").toLowerCase();
  return t.includes("practice") || t.includes("overall") || t.includes("employability");
};

/** Custom logic: lock edits once any slot has a saved question paper. */
const locksLogicAfterQuestions = (
  testType?: string | null,
  subjectCount = 0
) => {
  const t = (testType || "").trim().toLowerCase();
  if (t === "practice test" || t === "overall final test") return true;
  return t === "final test" && subjectCount > 1;
};

const QuestionSelectionLogicModal: React.FC<QuestionSelectionLogicModalProps> = ({
  show,
  testId,
  testName,
  testType,
  subjects,
  batchNames = [],
  readOnly = false,
  onHide,
  onSaved,
}) => {
  const isMultiSubjectFinal =
    (testType || "").trim() === "Final Test" && subjects.length > 1;
  const multiSubject = allowsMultiSubject(testType) || isMultiSubjectFinal;
  const [logic, setLogic] = useState<QuestionSelectionLogic>(emptyLogic);
  const [savedLogic, setSavedLogic] = useState<QuestionSelectionLogic | null>(null);
  const [logicSaved, setLogicSaved] = useState(false);
  const [questionsLocked, setQuestionsLocked] = useState(false);
  const [topics, setTopics] = useState<ContentTopic[]>([]);
  const [subtopics, setSubtopics] = useState<
    (ContentSubtopic & { subject_id: string; topic_id: string })[]
  >([]);
  const [availability, setAvailability] = useState<Record<string, Availability>>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lmsRules, setLmsRules] = useState<LmsRulesByType | null>(null);
  const effectiveReadOnly = readOnly || questionsLocked;

  const subjectOptions = subjects.map((s) => ({
    value: s.subject_id,
    label: s.subject_name,
  }));

  const loadMeta = useCallback(async (subjectIds: string[], topicIds: string[]) => {
    const topicLists = await Promise.all(
      subjectIds.map(async (id) => {
        const rows = await fetchTopicsForSubject(id);
        return rows.map((t) => ({ ...t, subject_id: t.subject_id || id }));
      })
    );
    const allTopics = topicLists.flat();
    setTopics(allTopics);

    const validTopicIds = topicIds.filter((tid) =>
      allTopics.some((t) => t.topic_id === tid)
    );
    const subLists = await Promise.all(
      validTopicIds.map(async (tid) => {
        const topic = allTopics.find((t) => t.topic_id === tid);
        const rows = await fetchSubtopicsForTopic(tid);
        return rows.map((s) => ({
          ...s,
          topic_id: tid,
          subject_id: topic?.subject_id || "",
        }));
      })
    );
    setSubtopics(subLists.flat());
    return { allTopics, allSubtopics: subLists.flat() };
  }, []);

  const rebuildRules = (
    next: QuestionSelectionLogic,
    allSubtopics: typeof subtopics,
    allTopics: ContentTopic[]
  ): QuestionSelectionLogic => {
    const rules: SubtopicQuestionRule[] = next.sub_topic_ids.map((sid) => {
      const existing = next.subtopic_rules.find((r) => r.sub_topic_id === sid);
      const meta = allSubtopics.find((s) => s.sub_topic_id === sid);
      const subject = subjects.find((s) => s.subject_id === meta?.subject_id);
      const topic = allTopics.find((t) => t.topic_id === meta?.topic_id);
      return {
        subject_id: meta?.subject_id || existing?.subject_id || "",
        topic_id: meta?.topic_id || existing?.topic_id || "",
        sub_topic_id: sid,
        subject_name: subject?.subject_name || existing?.subject_name,
        topic_name: topic?.topic_name || existing?.topic_name,
        sub_topic_name: meta?.sub_topic_name || existing?.sub_topic_name,
        level_counts: existing?.level_counts?.length
          ? existing.level_counts
          : defaultLevelCounts(),
      };
    });
    return { ...next, subtopic_rules: rules };
  };

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setQuestionsLocked(false);
      try {
        const [res, slotsRes] = await Promise.all([
          authService.get(`${TEST_FLOW_API}/tests/${testId}/selection-logic`),
          locksLogicAfterQuestions(testType, subjects.length)
            ? authService.get(`${TEST_FLOW_API}/tests/${testId}/slots`)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (slotsRes) {
          const slots: Array<{ question_count?: number }> = Array.isArray(
            slotsRes.data?.slots
          )
            ? slotsRes.data.slots
            : [];
          const hasSavedQuestions = slots.some(
            (s) => Number(s.question_count || 0) > 0
          );
          setQuestionsLocked(hasSavedQuestions);
        }
        const loaded: QuestionSelectionLogic = res.data?.logic || {
          ...emptyLogic(),
          subject_ids: subjects.map((s) => s.subject_id),
        };
        if (!loaded.subject_ids?.length) {
          loaded.subject_ids = subjects.map((s) => s.subject_id);
        }
        const { allTopics, allSubtopics } = await loadMeta(
          loaded.subject_ids,
          loaded.topic_ids || []
        );
        if (cancelled) return;
        const normalized = rebuildRules(loaded, allSubtopics, allTopics);
        setLogic(normalized);
        const hasSaved = Boolean(res.data?.logic);
        setSavedLogic(hasSaved ? normalized : null);
        setLogicSaved(hasSaved);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Failed to load selection logic.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, testId, testType, subjects, loadMeta]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.get("admin/fetch_rules/");
        if (!cancelled) setLmsRules(res.data || null);
      } catch {
        if (!cancelled) setLmsRules(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show || !logic.sub_topic_ids.length) {
      setAvailability({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchQuestionCountsBySubtopics(logic.sub_topic_ids);
        if (!cancelled) setAvailability(next);
      } catch {
        if (!cancelled) {
          const fallback: Record<string, Availability> = {};
          for (const sid of logic.sub_topic_ids) {
            fallback[sid] = {
              mcq: { level1: 0, level2: 0, level3: 0 },
              coding: { level1: 0, level2: 0, level3: 0 },
            };
          }
          setAvailability(fallback);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, logic.sub_topic_ids]);

  const topicOptions = useMemo(() => {
    const selected = new Set(logic.subject_ids);
    return topics
      .filter((t) => !t.subject_id || selected.has(t.subject_id))
      .map((t) => ({
        value: t.topic_id,
        label: t.subject_name
          ? `${t.subject_name} · ${t.topic_name}`
          : t.topic_name,
      }));
  }, [topics, logic.subject_ids]);

  /** PT / OFT: group topic dropdown options under subject headers. */
  const topicGroups = useMemo(() => {
    if (!multiSubject) return undefined;
    const selected = new Set(logic.subject_ids);
    const filtered = topics.filter(
      (t) => !t.subject_id || selected.has(t.subject_id)
    );
    const bySubject = new Map<
      string,
      { label: string; options: { value: string; label: string }[] }
    >();

    for (const t of filtered) {
      const subjectId = t.subject_id || "unknown";
      const subjectName =
        subjects.find((sub) => sub.subject_id === subjectId)?.subject_name ||
        t.subject_name ||
        subjectId;
      if (!bySubject.has(subjectId)) {
        bySubject.set(subjectId, { label: subjectName, options: [] });
      }
      bySubject.get(subjectId)!.options.push({
        value: t.topic_id,
        label: t.topic_name,
      });
    }

    const ordered: {
      label: string;
      options: { value: string; label: string }[];
    }[] = [];
    for (const sid of logic.subject_ids) {
      const group = bySubject.get(sid);
      if (group?.options.length) ordered.push(group);
    }
    Array.from(bySubject.entries()).forEach(([sid, group]) => {
      if (!logic.subject_ids.includes(sid) && group.options.length) {
        ordered.push(group);
      }
    });
    return ordered;
  }, [multiSubject, topics, logic.subject_ids, subjects]);

  const subtopicOptions = useMemo(
    () =>
      subtopics
        .filter((s) => logic.topic_ids.includes(s.topic_id))
        .map((s) => ({
          value: s.sub_topic_id,
          label: s.sub_topic_name,
        })),
    [subtopics, logic.topic_ids]
  );

  /** Group subtopics under subject → topic headers. */
  const subtopicGroups = useMemo(() => {
    if (!multiSubject) return undefined;
    const filtered = subtopics.filter((s) =>
      logic.topic_ids.includes(s.topic_id)
    );

    type TopicBucket = {
      label: string;
      options: { value: string; label: string }[];
    };
    type SubjectBucket = {
      label: string;
      topics: Map<string, TopicBucket>;
    };

    const bySubject = new Map<string, SubjectBucket>();

    for (const s of filtered) {
      const topicId = s.topic_id || "unknown";
      const topicMeta = topics.find((t) => t.topic_id === topicId);
      const subjectId =
        s.subject_id || topicMeta?.subject_id || "unknown";
      const subjectName =
        subjects.find((sub) => sub.subject_id === subjectId)?.subject_name ||
        topicMeta?.subject_name ||
        subjectId;
      const topicName = topicMeta?.topic_name || topicId;

      if (!bySubject.has(subjectId)) {
        bySubject.set(subjectId, {
          label: subjectName,
          topics: new Map(),
        });
      }
      const subjectBucket = bySubject.get(subjectId)!;
      if (!subjectBucket.topics.has(topicId)) {
        subjectBucket.topics.set(topicId, {
          label: topicName,
          options: [],
        });
      }
      subjectBucket.topics.get(topicId)!.options.push({
        value: s.sub_topic_id,
        label: s.sub_topic_name,
      });
    }

    const ordered: {
      label: string;
      subgroups: { label: string; options: { value: string; label: string }[] }[];
    }[] = [];

    const pushSubject = (subjectId: string) => {
      const bucket = bySubject.get(subjectId);
      if (!bucket) return;
      const subgroups: {
        label: string;
        options: { value: string; label: string }[];
      }[] = [];
      for (const tid of logic.topic_ids) {
        const topic = bucket.topics.get(tid);
        if (topic?.options.length) subgroups.push(topic);
      }
      Array.from(bucket.topics.entries()).forEach(([tid, topic]) => {
        if (!logic.topic_ids.includes(tid) && topic.options.length) {
          subgroups.push(topic);
        }
      });
      if (subgroups.length) {
        ordered.push({ label: bucket.label, subgroups });
      }
    };

    for (const sid of logic.subject_ids) {
      pushSubject(sid);
    }
    Array.from(bySubject.keys()).forEach((sid) => {
      if (!logic.subject_ids.includes(sid)) {
        pushSubject(sid);
      }
    });
    return ordered;
  }, [
    multiSubject,
    subtopics,
    logic.topic_ids,
    logic.subject_ids,
    subjects,
    topics,
  ]);

  const totals = useMemo(() => {
    let mcq = 0;
    let coding = 0;
    let availMcq = 0;
    let availCoding = 0;
    logic.subtopic_rules.forEach((rule) => {
      const avail = availability[rule.sub_topic_id];
      rule.level_counts.forEach((lc) => {
        mcq += lc.mcq_count;
        coding += lc.coding_count;
        const level = lc.level as keyof LevelCountMap;
        availMcq += Number(avail?.mcq?.[level] || 0);
        availCoding += Number(avail?.coding?.[level] || 0);
      });
    });
    return { mcq, coding, availMcq, availCoding, total: mcq + coding };
  }, [logic.subtopic_rules, availability]);

  const paperTotals = useMemo(
    () => computePaperTotalsFromLogic(logic, lmsRules),
    [logic, lmsRules]
  );

  const showPaperTotals = totals.total > 0;

  const markDirty = (next: QuestionSelectionLogic) => {
    setLogic(next);
    if (logicSaved && savedLogic && !logicEquals(next, savedLogic)) {
      setLogicSaved(false);
    }
  };

  const handleSubjectsChange = async (subjectIds: string[]) => {
    const next = {
      ...logic,
      subject_ids: subjectIds,
      topic_ids: [],
      sub_topic_ids: [],
      subtopic_rules: [],
    };
    markDirty(next);
    setSavedLogic(null);
    setLogicSaved(false);
    await loadMeta(subjectIds, []);
  };

  const handleTopicsChange = async (topicIds: string[]) => {
    const nextBase = {
      ...logic,
      topic_ids: topicIds,
      sub_topic_ids: [],
      subtopic_rules: [],
    };
    markDirty(nextBase);
    await loadMeta(logic.subject_ids, topicIds);
  };

  const handleSubtopicsChange = (subTopicIds: string[]) => {
    markDirty(rebuildRules({ ...logic, sub_topic_ids: subTopicIds }, subtopics, topics));
  };

  const setCount = (
    subTopicId: string,
    level: string,
    field: "mcq_count" | "coding_count",
    value: number
  ) => {
    markDirty({
      ...logic,
      subtopic_rules: logic.subtopic_rules.map((rule) => {
        if (rule.sub_topic_id !== subTopicId) return rule;
        return {
          ...rule,
          level_counts: rule.level_counts.map((lc) => {
            if (lc.level !== level) return lc;
            const available = availability[subTopicId];
            const max =
              field === "mcq_count"
                ? Number(available?.mcq?.[level as keyof LevelCountMap] || 0)
                : Number(available?.coding?.[level as keyof LevelCountMap] || 0);
            return {
              ...lc,
              [field]: Math.max(0, Math.min(Number.isFinite(value) ? value : 0, max)),
            };
          }),
        };
      }),
    });
  };

  const canSave =
    logic.subject_ids.length > 0 &&
    logic.topic_ids.length > 0 &&
    totals.total > 0;

  const handleSave = async () => {
    if (effectiveReadOnly || !canSave) return;
    setSaving(true);
    setError("");
    try {
      const payload = rebuildRules(logic, subtopics, topics);
      const res = await authService.put(
        `${TEST_FLOW_API}/tests/${testId}/selection-logic`,
        { logic: payload }
      );
      setSavedLogic(payload);
      setLogicSaved(true);
      onSaved(payload, res.data?.description || undefined, {
        duration: paperTotals.minutes,
        marks: paperTotals.marks,
      });
      onHide();
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to save selection logic.";
      setError(String(detail));
    } finally {
      setSaving(false);
    }
  };

  const subjectLabel = subjects
    .filter((s) => logic.subject_ids.includes(s.subject_id))
    .map((s) => s.subject_name)
    .join(" · ");

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="question-selection-logic-modal"
      className="test-flow-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Selection Logic — {testName || "Test"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3 d-flex flex-column">
        {error && <Alert variant="danger">{error}</Alert>}
        {questionsLocked && (
          <Alert variant="warning" className="mb-3">
            Selection logic cannot be changed after questions are saved for a
            slot.
          </Alert>
        )}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="card shadow-sm border-0 selection-logic-layout">
            <div className="card-body p-3">
              <div className="selection-logic-filters">
                <Row className="g-3 mb-1">
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                      </div>
                      {multiSubject ? (
                        <TestMultiSelect
                          label="Subjects"
                          value={logic.subject_ids}
                          onChange={handleSubjectsChange}
                          options={subjectOptions}
                          placeholder="Select subject(s)"
                          disabled={effectiveReadOnly}
                        />
                      ) : (
                        <Form.Select
                          value={logic.subject_ids[0] || ""}
                          disabled={effectiveReadOnly}
                          onChange={(e) =>
                            handleSubjectsChange(
                              e.target.value ? [e.target.value] : []
                            )
                          }
                        >
                          <option value="">Select subject</option>
                          {subjectOptions.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <TestMultiSelect
                        label="Topics"
                        value={logic.topic_ids}
                        onChange={handleTopicsChange}
                        options={topicOptions}
                        groups={multiSubject ? topicGroups : undefined}
                        disabled={effectiveReadOnly || !logic.subject_ids.length}
                        placeholder={
                          logic.subject_ids.length === 0
                            ? "Select subject(s) first"
                            : "Select topic(s)"
                        }
                      />
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <div className="d-flex gap-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() =>
                              handleTopicsChange(
                                multiSubject
                                  ? (topicGroups || []).flatMap((g) =>
                                      g.options.map((o) => o.value)
                                    )
                                  : topicOptions.map((o) => o.value)
                              )
                            }
                            disabled={
                              effectiveReadOnly ||
                              (multiSubject
                                ? !(topicGroups || []).some((g) => g.options.length)
                                : topicOptions.length === 0)
                            }
                          >
                            All
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none text-muted"
                            onClick={() => handleTopicsChange([])}
                            disabled={
                              effectiveReadOnly ||
                              (multiSubject
                                ? !(topicGroups || []).some((g) => g.options.length)
                                : topicOptions.length === 0)
                            }
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <TestMultiSelect
                        label="Subtopics"
                        value={logic.sub_topic_ids}
                        onChange={handleSubtopicsChange}
                        options={subtopicOptions}
                        groups={multiSubject ? subtopicGroups : undefined}
                        disabled={effectiveReadOnly || !logic.topic_ids.length}
                        placeholder={
                          logic.topic_ids.length === 0
                            ? "Select topics first"
                            : "Select subtopic(s)"
                        }
                      />
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <div className="d-flex gap-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() =>
                              handleSubtopicsChange(
                                multiSubject
                                  ? (subtopicGroups || []).flatMap((g) =>
                                      (g.subgroups || []).flatMap((sg) =>
                                        sg.options.map((o) => o.value)
                                      )
                                    )
                                  : subtopicOptions.map((o) => o.value)
                              )
                            }
                            disabled={effectiveReadOnly || logic.topic_ids.length === 0}
                          >
                            All
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none text-muted"
                            onClick={() => handleSubtopicsChange([])}
                            disabled={effectiveReadOnly || logic.topic_ids.length === 0}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div
                className={`selection-logic-table-wrap mt-3${
                  logic.subtopic_rules.length > 0 ? " border rounded" : ""
                }`}
              >
                  {logic.subtopic_rules.length === 0 ? (
                    <div className="text-muted small border rounded p-3">
                      Select topics and subtopics to configure question counts per
                      level.
                    </div>
                  ) : (
                      <Table
                        size="sm"
                        bordered
                        className="mb-0 align-middle selection-logic-table"
                      >
                        <thead className="table-light">
                          <tr>
                            {multiSubject && (
                              <th rowSpan={2} className="align-middle">
                                Subject
                              </th>
                            )}
                            <th rowSpan={2} className="align-middle">
                              Topic
                            </th>
                            <th rowSpan={2} className="align-middle">
                              Subtopic
                            </th>
                            <th rowSpan={2} className="align-middle">
                              Level
                            </th>
                            <th colSpan={2} className="text-center border-start">
                              MCQ
                            </th>
                            <th colSpan={2} className="text-center border-start">
                              Coding
                            </th>
                          </tr>
                          <tr>
                            <th className="text-center small fw-semibold text-nowrap border-start">
                              Selected
                            </th>
                            <th className="text-center small fw-semibold text-nowrap">
                              Available
                            </th>
                            <th className="text-center small fw-semibold text-nowrap border-start">
                              Selected
                            </th>
                            <th className="text-center small fw-semibold text-nowrap">
                              Available
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {logic.subtopic_rules.map((rule) =>
                            LEVELS.map((level, levelIndex) => {
                              const lc =
                                rule.level_counts.find((x) => x.level === level) || {
                                  level,
                                  mcq_count: 0,
                                  coding_count: 0,
                                };
                              const avail = availability[rule.sub_topic_id];
                              const mcqAvail = Number(avail?.mcq?.[level] ?? 0);
                              const codingAvail = Number(
                                avail?.coding?.[level] ?? 0
                              );
                              return (
                                <tr key={`${rule.sub_topic_id}-${level}`}>
                                  {levelIndex === 0 && (
                                    <>
                                      {multiSubject && (
                                        <td
                                          rowSpan={LEVELS.length}
                                          className="small fw-semibold"
                                        >
                                          {rule.subject_name}
                                        </td>
                                      )}
                                      <td
                                        rowSpan={LEVELS.length}
                                        className="small"
                                      >
                                        {rule.topic_name}
                                      </td>
                                      <td
                                        rowSpan={LEVELS.length}
                                        className="fw-semibold small"
                                      >
                                        {rule.sub_topic_name}
                                      </td>
                                    </>
                                  )}
                                  <td className="small text-nowrap">
                                    <Badge bg="primary" className="me-1">
                                      L{level.replace("level", "")}
                                    </Badge>
                                    <span className="text-muted">
                                      {LEVEL_LABELS[level]}
                                    </span>
                                  </td>
                                  <td className="p-1 text-center">
                                    <Form.Control
                                      type="number"
                                      size="sm"
                                      min={0}
                                      max={mcqAvail}
                                      value={lc.mcq_count}
                                      disabled={effectiveReadOnly}
                                      readOnly={effectiveReadOnly}
                                      className="text-center mx-auto w-auto"
                                      onChange={(e) =>
                                        setCount(
                                          rule.sub_topic_id,
                                          level,
                                          "mcq_count",
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="text-center">
                                    <Badge
                                      bg={mcqAvail > 0 ? "secondary" : "light"}
                                      text={mcqAvail > 0 ? "white" : "dark"}
                                    >
                                      {mcqAvail}
                                    </Badge>
                                  </td>
                                  <td className="p-1 text-center">
                                    <Form.Control
                                      type="number"
                                      size="sm"
                                      min={0}
                                      max={codingAvail}
                                      value={lc.coding_count}
                                      disabled={effectiveReadOnly}
                                      readOnly={effectiveReadOnly}
                                      className="text-center mx-auto w-auto"
                                      onChange={(e) =>
                                        setCount(
                                          rule.sub_topic_id,
                                          level,
                                          "coding_count",
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="text-center">
                                    <Badge
                                      bg={codingAvail > 0 ? "secondary" : "light"}
                                      text={codingAvail > 0 ? "white" : "dark"}
                                    >
                                      {codingAvail}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td
                              colSpan={multiSubject ? 4 : 3}
                              className="fw-semibold small align-middle"
                            >
                              Total to assign
                            </td>
                            <td className="text-center align-middle border-start">
                              <Badge bg="primary">{totals.mcq}</Badge>
                            </td>
                            <td className="text-center align-middle">
                              <Badge bg="secondary">{totals.availMcq}</Badge>
                            </td>
                            <td className="text-center align-middle border-start">
                              <Badge bg="primary">{totals.coding}</Badge>
                            </td>
                            <td className="text-center align-middle">
                              <Badge bg="secondary">{totals.availCoding}</Badge>
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                  )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex flex-wrap gap-3 small text-secondary">
          {showPaperTotals && (
            <>
              <span className="d-inline-flex align-items-center gap-1">
                <BsClock />
                {paperTotals.minutes}{" "}
                {paperTotals.minutes === 1 ? "Minute" : "Minutes"}
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <GiStarShuriken />
                {paperTotals.marks} {paperTotals.marks === 1 ? "Mark" : "Marks"}
              </span>
            </>
          )}
        </div>
        <div className="d-flex gap-2 ms-auto">
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Close
          </Button>
          {!effectiveReadOnly && (
            <Button
              variant="primary"
              disabled={!canSave || saving || loading}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save Logic & Close"}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionSelectionLogicModal;
